/*
 * Product gallery video autoplay.
 *
 * Keeps at most one video playing per product gallery, based on the gallery
 * type resolved for the current breakpoint:
 *   - slider galleries (Horizontal Slider, Horizontal Carousel, Slider with
 *     Bullets, Slider with Previews) -> the video of the active slide, if any;
 *   - Grid Gallery / Stacked Gallery -> the first video of the gallery.
 * Every other video of the gallery is paused, and a video is only loaded once
 * it becomes an autoplay target.
 *
 * The Horizontal Carousel shows several slides at once and has a desktop only
 * option of its own, which replaces that single target by every video actually
 * visible in the carousel viewport, tracked with an IntersectionObserver.
 *
 * Galleries opt in through the "Enable video autoplay" section setting: without
 * it the markup carries no autoplay data attributes and videos keep playing on
 * click only.
 */
(function () {
  if (window.ProductGalleryAutoplay) return;

  const DESKTOP_MEDIA_QUERY = "(min-width: 750px)";
  const STATIC_GALLERY_TYPES = [
    "static_multicolumns_aside",
    "static_column_aside",
  ];
  // Only galleries whose section has the autoplay setting turned on are managed.
  const ROOT_SELECTOR = '.product__media-wrapper[data-gallery-autoplay="true"]';
  // Galleries cloned into a modal keep their original click-to-play behaviour.
  const EXCLUDED_CONTEXTS =
    "quick-add-modal, product-modal, .product-media-modal, #quick-view";
  const OPEN_MODAL_SELECTOR =
    "quick-add-modal[open], product-modal[open], modal-dialog[open]";
  const NOSCRIPT_SELECTOR = ".product__media-grid-noscript";
  const MEDIA_HOST_TAGS = ["DEFERRED-MEDIA", "PRODUCT-MODEL"];
  const SLIDER_SELECTOR = ".js-media-list, .js-media-list-mobile";
  const CAROUSEL_GALLERY_TYPE = "multiple_top";
  // Share of a slide that has to sit inside the carousel viewport for its video
  // to count as visible, so slivers scrolling by are not started.
  const VISIBLE_RATIO = 0.25;
  const SYNC_DELAY = 120;
  const BREAKPOINT_SYNC_DELAY = 400;

  const desktopQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
  const observedRoots = new WeakSet();
  const carouselObservers = new WeakMap();
  let syncTimer = null;

  const isModalOpen = () =>
    document.querySelector(OPEN_MODAL_SELECTOR) !== null;

  const getRoots = () =>
    Array.from(document.querySelectorAll(ROOT_SELECTOR)).filter(
      (root) => !root.closest(EXCLUDED_CONTEXTS)
    );

  const getGalleryType = (root) => {
    if (desktopQuery.matches) return root.dataset.galleryType || "";

    return root.dataset.galleryTypeMobile || root.dataset.galleryType || "";
  };

  const isStaticGallery = (root) =>
    STATIC_GALLERY_TYPES.indexOf(getGalleryType(root)) !== -1;

  const isRenderedMedia = (element) => !element.closest(NOSCRIPT_SELECTOR);

  const getMediaItems = (root) =>
    Array.from(
      root.querySelectorAll(".product__media-list .product__media-item")
    ).filter(
      (item) =>
        isRenderedMedia(item) &&
        !item.classList.contains("product__media-item--variant-hidden")
    );

  const getVideoType = (media) => {
    const element =
      media.querySelector("video, iframe") ||
      media.querySelector("template")?.content?.firstElementChild;

    if (!element) return null;
    if (element.tagName === "VIDEO") return "native";
    if (element.tagName !== "IFRAME") return null;
    if (element.classList.contains("js-youtube")) return "youtube";
    if (element.classList.contains("js-vimeo")) return "vimeo";

    return null;
  };

  const getVideos = (root) =>
    Array.from(root.querySelectorAll("deferred-media")).filter(
      (media) => isRenderedMedia(media) && getVideoType(media) !== null
    );

  const getItemVideo = (item) => {
    if (!item) return null;

    const media = item.querySelector("deferred-media");

    return media && getVideoType(media) ? media : null;
  };

  const getFirstVideo = (root) => {
    const items = getMediaItems(root);

    for (let index = 0; index < items.length; index++) {
      const media = getItemVideo(items[index]);
      if (media) return media;
    }

    return null;
  };

  const getActiveSlideVideo = (root) => {
    const slider = root.querySelector(SLIDER_SELECTOR);
    if (!slider) return getFirstVideo(root);

    const swiper = slider.swiper;
    const slides = swiper ? Array.from(swiper.slides) : getMediaItems(root);
    const activeIndex = swiper
      ? swiper.activeIndex
      : Number(slider.dataset.currentIndex || 0);

    return getItemVideo(slides[activeIndex]);
  };

  // Desktop only, and only for the gallery type the option belongs to.
  const isVisibleCarouselMode = (root) =>
    root.dataset.galleryAutoplayVisible === "true" &&
    desktopQuery.matches &&
    root.dataset.galleryType === CAROUSEL_GALLERY_TYPE;

  const getVideoItems = (root) =>
    getMediaItems(root).filter((item) => getItemVideo(item) !== null);

  const stopWatchingVisibility = (root) => {
    const watcher = carouselObservers.get(root);
    if (!watcher) return;

    watcher.observer.disconnect();
    carouselObservers.delete(root);
  };

  const isSameList = (a, b) =>
    a.length === b.length && a.every((element, index) => element === b[index]);

  // The carousel viewport is the slider element itself, so a slide counts as
  // visible only while it is actually inside it, whatever the active index is.
  const watchVisibility = (root) => {
    if (!isVisibleCarouselMode(root)) {
      stopWatchingVisibility(root);
      return;
    }

    const slider = root.querySelector(SLIDER_SELECTOR);
    const items = getVideoItems(root);

    if (!slider || !items.length) {
      stopWatchingVisibility(root);
      return;
    }

    const watcher = carouselObservers.get(root);
    if (
      watcher &&
      watcher.slider === slider &&
      isSameList(watcher.items, items)
    )
      return;

    stopWatchingVisibility(root);

    const visible = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio >= VISIBLE_RATIO)
            visible.add(entry.target);
          else visible.delete(entry.target);
        });

        syncRoot(root);
      },
      { root: slider, threshold: [0, VISIBLE_RATIO, 1] }
    );

    carouselObservers.set(root, { observer, slider, items, visible });
    items.forEach((item) => observer.observe(item));
  };

  const getVisibleCarouselVideos = (root) => {
    const watcher = carouselObservers.get(root);
    if (!watcher || !watcher.visible.size) return [];

    return watcher.items
      .filter((item) => watcher.visible.has(item))
      .map(getItemVideo)
      .filter((media) => media !== null);
  };

  const getTargetVideos = (root) => {
    if (isModalOpen()) return [];
    if (isVisibleCarouselMode(root)) return getVisibleCarouselVideos(root);

    const media = isStaticGallery(root)
      ? getFirstVideo(root)
      : getActiveSlideVideo(root);

    return media ? [media] : [];
  };

  const withMutedParam = (src, type) => {
    if (!src) return src;

    const param = type === "vimeo" ? "muted=1" : "mute=1";
    if (src.indexOf(param) !== -1) return src;

    return src + (src.indexOf("?") === -1 ? "?" : "&") + param;
  };

  const loadVideo = (media, type) => {
    const source = media.querySelector("template")?.content?.firstElementChild;
    if (!source) return null;

    const element = source.cloneNode(true);

    if (type === "native") {
      element.muted = true;
      element.setAttribute("muted", "");
      element.setAttribute("playsinline", "");
    } else {
      element.setAttribute(
        "src",
        withMutedParam(element.getAttribute("src"), type)
      );
    }

    media.setAttribute("loaded", true);
    media.appendChild(element);

    return element;
  };

  const playNativeVideo = (video) => {
    const played = video.play();
    if (!played || typeof played.catch !== "function") return;

    // Playback can still be refused, for instance once the visitor unmuted the
    // video. Muting is the only way to keep it running in that case.
    played.catch(() => {
      video.muted = true;
      const retried = video.play();
      if (retried && typeof retried.catch === "function") {
        retried.catch(() => {});
      }
    });
  };

  const postToPlayer = (iframe, message) => {
    if (!iframe || !iframe.contentWindow) return;

    iframe.contentWindow.postMessage(message, "*");
  };

  const playVideo = (media) => {
    const type = getVideoType(media);
    if (!type) return;

    const wasLoaded = media.hasAttribute("loaded");
    const element = wasLoaded
      ? media.querySelector("video, iframe")
      : loadVideo(media, type);

    if (!element) return;

    if (type === "native") {
      element.playsInline = true;
      playNativeVideo(element);
      return;
    }

    // A freshly appended iframe starts on its own, its src carries autoplay.
    if (!wasLoaded) return;

    if (type === "youtube") {
      postToPlayer(element, '{"event":"command","func":"playVideo","args":""}');
    } else {
      postToPlayer(element, '{"method":"play"}');
    }
  };

  // The theme pauses every media of the page on each slide change, so whether a
  // target still has to be started is read from the element itself rather than
  // remembered: anything that stopped it outside of this script is undone.
  const needsStart = (media) => {
    if (!media.hasAttribute("loaded")) return true;

    const video = media.querySelector("video");
    // External players cannot be queried, and asking them to play again while
    // they already do is a no-op.
    if (!video) return true;

    // A video that reached its end is left alone, looping it would go against
    // the "Enable video looping" setting.
    return video.paused && !video.ended;
  };

  const pauseVideo = (media) => {
    const type = getVideoType(media);
    if (!type || !media.hasAttribute("loaded")) return;

    if (type === "native") {
      media.querySelector("video")?.pause();
      return;
    }

    const iframe = media.querySelector("iframe");

    if (type === "youtube") {
      postToPlayer(iframe, '{"event":"command","func":"pauseVideo","args":""}');
    } else {
      postToPlayer(iframe, '{"method":"pause"}');
    }
  };

  const bindSlider = (root) => {
    const swiper = root.querySelector(SLIDER_SELECTOR)?.swiper;
    if (!swiper || swiper.galleryAutoplayBound) return;

    swiper.galleryAutoplayBound = true;
    // Synchronous, so that the videos which must keep playing are started back
    // in the same tick as the pauseAllMedia() the theme runs on a slide change.
    swiper.on("slideChange", () => syncRoot(root));
  };

  // A media element loading its own content, either from a visitor click on the
  // poster or from this script, must not trigger a new sync: the visitor choice
  // wins over the autoplay target.
  const isMediaLoadMutation = (record) =>
    record.type === "childList" &&
    MEDIA_HOST_TAGS.indexOf(record.target.tagName) !== -1;

  const observeRoot = (root) => {
    if (observedRoots.has(root)) return;
    observedRoots.add(root);

    // The gallery markup is replaced on variant change, and the variant media
    // grouping shows or hides its items through classes.
    const observer = new MutationObserver((records) => {
      if (records.every(isMediaLoadMutation)) return;

      scheduleSync();
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });
  };

  const syncRoot = (root) => {
    const videos = getVideos(root);
    if (!videos.length) {
      stopWatchingVisibility(root);
      return;
    }

    bindSlider(root);
    watchVisibility(root);

    const targets = getTargetVideos(root);

    videos.forEach((media) => {
      if (targets.indexOf(media) === -1) pauseVideo(media);
    });

    targets.forEach((media) => {
      if (needsStart(media)) playVideo(media);
    });
  };

  const sync = () => {
    getRoots().forEach((root) => {
      observeRoot(root);
      syncRoot(root);
    });
  };

  const pauseAll = () => {
    getRoots().forEach((root) => getVideos(root).forEach(pauseVideo));
  };

  const scheduleSync = (delay) => {
    if (syncTimer) clearTimeout(syncTimer);

    syncTimer = window.setTimeout(
      () => {
        syncTimer = null;
        sync();
      },
      typeof delay === "number" ? delay : SYNC_DELAY
    );
  };

  const onBreakpointChange = () => {
    // The resolved gallery type changes with the breakpoint: stop everything
    // before the layout of the new one settles.
    pauseAll();
    scheduleSync(BREAKPOINT_SYNC_DELAY);
  };

  if (desktopQuery.addEventListener) {
    desktopQuery.addEventListener("change", onBreakpointChange);
  } else if (desktopQuery.addListener) {
    desktopQuery.addListener(onBreakpointChange);
  }

  document.addEventListener("shopify:section:load", () => scheduleSync());
  document.addEventListener("shopify:section:select", () => scheduleSync());
  // Opening a modal pauses every media of the page, so the gallery has to pick
  // its targets up again once the modal is closed.
  document.body?.addEventListener("modalClosed", () => scheduleSync());

  window.ProductGalleryAutoplay = { sync, pauseAll, refresh: scheduleSync };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => sync());
  } else {
    sync();
  }
})();
